import { SearchSeriesSortCriteria } from 'models/search';
import { encodeSportFilter, slugify } from 'utils/sports';

export const categories = [
    'elections',
    'economics',
    'politics',
    'culture',
    'crypto',
    'commodities',
    'financials',
    'science',
    'climate',
    'sports',
    'general',
    'mentions',
] as const;

export enum BECategory {
    ECONOMICS = 'Economics',
    FINANCIALS = 'Financials',
    CRYPTO = 'Crypto',
    POLITICS = 'Politics',
    CLIMATE = 'Climate and Weather',
    CULTURE = 'Entertainment',
    SCIENCE = 'Science and Technology',
    ELECTIONS = 'Elections',
    SPORTS = 'Sports',
    COMMODITIES = 'Commodities',
    MENTIONS = 'Mentions',
    GENERAL = 'Other',
}

export type Category = (typeof categories)[number];

export const CATEGORIES_WITH_DEDICATED_ROUTES: Category[] = ['crypto', 'commodities', 'sports'];

export const NON_ROUTABLE_CATEGORIES: Category[] = ['general'];

export const isGenericSlugCategory = (category: Category): boolean => {
    return (
        categories.includes(category) &&
        !NON_ROUTABLE_CATEGORIES.includes(category) &&
        !CATEGORIES_WITH_DEDICATED_ROUTES.includes(category)
    );
};

/**
 * Explore categories configuration for the public home page.
 * Each entry contains either:
 * - beCategory for category-based filtering
 * - searchTerm for search query-based filtering
 * Optionally supports sports filtering via tag and competition.
 */
export interface ExploreCategoryConfig {
    /** Backend category for API filtering (category slug derived via parseBECategoryToCategory) */
    beCategory?: BECategory;
    /** Optional custom display label (defaults to competition, searchTerm, or shortCategoryLabels[category]) */
    label?: string;
    /** Optional custom href for the header link (defaults to /category/{category} or /search?q={searchTerm}) */
    href?: string;
    /** Optional sport tag for API filtering (e.g., 'Football') */
    tag?: string;
    /** Optional competition for API filtering and URL generation (e.g., 'Pro Football') */
    competition?: string;
    /** Optional search term for query-based filtering (e.g., 'Pop Culture') */
    searchTerm?: string;
    /** Optional fuzzy threshold for search queries (defaults to 4) */
    fuzzyThreshold?: number;
}

/**
 * Resolves the category slug from an ExploreCategoryConfig.
 * Returns null for search-term based configs without a beCategory.
 */
export const resolveConfigCategory = (config: ExploreCategoryConfig): Category | null => {
    if (config.beCategory) {
        return parseBECategoryToCategory(config.beCategory);
    }
    return null;
};

/**
 * Resolves the display label from an ExploreCategoryConfig.
 * Priority: label > competition > searchTerm > tag > shortCategoryLabels[category]
 */
export const resolveConfigLabel = (config: ExploreCategoryConfig): string => {
    if (config.label) {
        return config.label;
    }
    if (config.competition) {
        return config.competition;
    }
    if (config.searchTerm) {
        return config.searchTerm;
    }
    if (config.tag) {
        return config.tag;
    }
    const category = resolveConfigCategory(config);
    return category ? shortCategoryLabels[category] : '';
};

/**
 * Resolves the href from an ExploreCategoryConfig.
 * For search terms, generates /search?q={searchTerm} URL.
 * For sports with tag and competition, generates /category/sports/{tag}/{competition} URL.
 * For category with tag (no competition), generates /search/{category}/{tag} URL.
 * Falls back to /category/{category} if no custom href is provided.
 */
export const resolveConfigHref = (config: ExploreCategoryConfig): string => {
    // Use explicit href if provided
    if (config.href) {
        return config.href;
    }
    // Generate search URL if searchTerm is provided
    if (config.searchTerm) {
        return `/search?q=${encodeURIComponent(config.searchTerm)}&order_by=${SearchSeriesSortCriteria.QUERY_MATCH}`;
    }
    // Generate sports competition URL if tag and competition are provided
    if (config.tag && config.competition) {
        return `/category/sports/${encodeSportFilter(config.tag)}/${slugify(config.competition)}`;
    }
    const category = resolveConfigCategory(config);
    // Generate category/tag URL if tag is provided without competition
    if (config.tag && category) {
        return `/category/${category}/${config.tag.toLowerCase()}`;
    }
    // Default to category page (or /browse if no category)
    return category ? `/category/${category}` : '/browse';
};

/**
 * Generates a unique key for an ExploreCategoryConfig.
 * Used for React keys and cache identifiers.
 * Ensures no collisions between different config types.
 */
export const getConfigKey = (config: ExploreCategoryConfig): string => {
    // For search term configs, use 'search:' prefix
    if (config.searchTerm) {
        return `search:${config.searchTerm}`;
    }
    // For category configs, build key from all relevant fields
    const parts = [config.beCategory ?? 'none'];
    if (config.tag) parts.push(`tag:${config.tag}`);
    if (config.competition) parts.push(`comp:${config.competition}`);
    return parts.join('|');
};

export const EXPLORE_CATEGORIES: ExploreCategoryConfig[] = [
    { beCategory: BECategory.SPORTS, tag: 'Football', competition: 'Pro Football' },
    { beCategory: BECategory.POLITICS },
    { searchTerm: 'Pop Culture' },
    { searchTerm: 'Corporate Finance' },
    { beCategory: BECategory.CRYPTO },
    { beCategory: BECategory.SCIENCE, tag: 'AI' },
];

export const shortCategoryLabels: Record<Category, string> = {
    financials: 'Finance',
    crypto: 'Crypto',
    commodities: 'Commodities',
    economics: 'Economics',
    politics: 'Politics',
    climate: 'Climate',
    culture: 'Culture',
    elections: 'Elections',
    sports: 'Sports',
    science: 'Tech & Science',
    mentions: 'Mentions',
    general: 'General',
};

// valid categories, ordered for the category selector
export const ORDERED_CATEGORIES: Category[] = [
    'elections',
    'politics',
    'sports',
    'culture',
    'crypto',
    'commodities',
    'climate',
    'economics',
    'mentions',
    'financials',
    'science',
];

export const parseBECategoryToCategory = (category?: BECategory): Category => {
    if (!category) {
        return 'general';
    }

    switch (category) {
        case BECategory.ECONOMICS:
            return 'economics';
        case BECategory.FINANCIALS:
            return 'financials';
        case BECategory.CRYPTO:
            return 'crypto';
        case BECategory.COMMODITIES:
            return 'commodities';
        case BECategory.CULTURE:
            return 'culture';
        case BECategory.POLITICS:
            return 'politics';
        case BECategory.CLIMATE:
            return 'climate';
        case BECategory.SCIENCE:
            return 'science';
        case BECategory.ELECTIONS:
            return 'elections';
        case BECategory.SPORTS:
            return 'sports';
        case BECategory.MENTIONS:
            return 'mentions';
        default:
            return 'general';
    }
};

// Sports has no `/category/sports` landing page; route to the all-sports hub.
export const getCategoryUrl = (category: Category): string =>
    category === 'sports' ? '/sports/all-sports' : `/category/${category}`;

export const parseCategoryToBECategory = (category?: Category): BECategory => {
    if (!category) {
        return BECategory.GENERAL;
    }

    switch (category) {
        case 'economics':
            return BECategory.ECONOMICS;
        case 'financials':
            return BECategory.FINANCIALS;
        case 'crypto':
            return BECategory.CRYPTO;
        case 'commodities':
            return BECategory.COMMODITIES;
        case 'culture':
            return BECategory.CULTURE;
        case 'politics':
            return BECategory.POLITICS;
        case 'climate':
            return BECategory.CLIMATE;
        case 'science':
            return BECategory.SCIENCE;
        case 'elections':
            return BECategory.ELECTIONS;
        case 'sports':
            return BECategory.SPORTS;
        case 'mentions':
            return BECategory.MENTIONS;
        default:
            return BECategory.GENERAL;
    }
};

/**
 * Normalizes a BECategory to the one used for routing.
 * Some categories (e.g. Elections) don't have their own route and
 * are served under a sibling category's route instead.
 */
export const normalizeBECategoryForRoute = (category: BECategory): BECategory => {
    return category;
};
